/**
 * Reference-layer manager: draws the tenant's layer catalog (lib/layers.ts)
 * on a live map -- outlines with a soft white casing so they read over
 * district colors and satellite imagery, plus name labels.
 *
 * Every layer is read-only context. Nothing here writes feature-state or
 * listens for clicks, so painting, validation and drawing never see these
 * layers. Toggling is instant (layout visibility, no re-fetch).
 *
 * Per catalog entry:
 *   overlay-casing-<id>  white line under the outline (legibility)
 *   overlay-line-<id>    the outline itself (color / width / dash from the catalog)
 *   overlay-hit-<id>     invisible fill, for "which tract/city am I in?" on hover
 *   overlay-label-<id>   names, from the catalog's label properties
 *
 * Sources: PMTiles archives are shared by URL (cities and census-designated
 * places come from one archive, split by filter); staff-uploaded district
 * maps are GeoJSON, filled in with `setReferenceData` once fetched.
 *
 * Labels come from one point per feature -- the archive's `<layer>_label`
 * points, or points computed here for GeoJSON maps -- so a name isn't
 * repeated on every tile-cut piece of a polygon.
 */
import type { FeatureCollection } from 'geojson';
import type maplibregl from 'maplibre-gl';
import type { LayerDef } from '../lib/layers';
import { LABEL_FONT } from './glyphs';
import { labelPoints } from './labelPoints';

export interface PlaceName {
  layerId: string;
  layerLabel: string;
  name: string;
  color: string;
}

export interface OverlayManager {
  setVisible: (id: string, visible: boolean) => void;
  /** GeoJSON for a staff-uploaded district map (catalog source type `reference`). */
  setReferenceData: (slug: string, geojson: FeatureCollection) => void;
  /** Names of the visible layers' features under a container point. */
  namesAt: (point: [number, number]) => PlaceName[];
  destroy: () => void;
}

type Filter = maplibregl.FilterSpecification;

function hash(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function sourceIdFor(layer: LayerDef): string {
  return layer.source.type === 'reference'
    ? `overlay-ref-${layer.source.slug}`
    : `overlay-src-${hash(layer.source.url ?? layer.id)}`;
}

function labelText(props: string[]): maplibregl.DataDrivenPropertyValueSpecification<string> {
  const gets = props.map((p) => ['to-string', ['get', p]]);
  return ['coalesce', ...gets, ''] as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>;
}

function nameOf(props: Record<string, unknown> | null | undefined, keys: string[]): string | null {
  if (!props) return null;
  for (const key of keys) {
    const value = props[key];
    const text = typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';
    if (text !== '') {
      // Uploaded maps often label districts with a bare number.
      return /^\d+$/.test(text) ? `District ${text}` : text;
    }
  }
  return null;
}

export function createOverlayManager(
  map: maplibregl.Map,
  layers: LayerDef[],
  options: { beforeId?: string } = {},
): OverlayManager {
  const before = (): string | undefined =>
    options.beforeId && map.getLayer(options.beforeId) ? options.beforeId : undefined;
  const added: { layer: LayerDef; ids: string[]; hitId: string }[] = [];
  const sources = new Set<string>();

  // Add bottom-up so the first catalog entries (district maps) end on top.
  for (const layer of [...layers].reverse()) {
    const sourceId = sourceIdFor(layer);
    if (!map.getSource(sourceId)) {
      if (layer.source.type === 'reference') {
        map.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      } else {
        map.addSource(sourceId, { type: 'vector', url: `pmtiles://${layer.source.url}` });
      }
    }
    sources.add(sourceId);
    // GeoJSON maps get a companion point source for their labels.
    const labelSourceId = layer.source.type === 'reference' ? `${sourceId}-labels` : sourceId;
    if (layer.source.type === 'reference' && !map.getSource(labelSourceId)) {
      map.addSource(labelSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    sources.add(labelSourceId);
    const common = {
      source: sourceId,
      ...(layer.source.type === 'pmtiles' && layer.source.sourceLayer
        ? { 'source-layer': layer.source.sourceLayer }
        : {}),
      ...(layer.filter ? { filter: layer.filter as unknown as Filter } : {}),
      ...(layer.minZoom !== null ? { minzoom: layer.minZoom } : {}),
    };
    const ids = {
      casing: `overlay-casing-${layer.id}`,
      line: `overlay-line-${layer.id}`,
      hit: `overlay-hit-${layer.id}`,
      label: `overlay-label-${layer.id}`,
    };
    const { color, width, dash, opacity } = layer.style;
    if (!map.getLayer(ids.hit)) {
      map.addLayer(
        { id: ids.hit, type: 'fill', ...common, layout: { visibility: 'none' }, paint: { 'fill-color': '#000', 'fill-opacity': 0 } },
        before(),
      );
    }
    if (!map.getLayer(ids.casing)) {
      map.addLayer(
        {
          id: ids.casing,
          type: 'line',
          ...common,
          layout: { visibility: 'none', 'line-join': 'round' },
          paint: { 'line-color': '#ffffff', 'line-width': width + 2.5, 'line-opacity': 0.6 },
        },
        before(),
      );
    }
    if (!map.getLayer(ids.line)) {
      map.addLayer(
        {
          id: ids.line,
          type: 'line',
          ...common,
          layout: { visibility: 'none', 'line-join': 'round' },
          paint: {
            'line-color': color,
            'line-width': width,
            'line-opacity': opacity,
            ...(dash && dash.length ? { 'line-dasharray': dash } : {}),
          },
        },
        before(),
      );
    }
    if (!map.getLayer(ids.label) && layer.labelProps.length) {
      const labelMin = layer.labelMinZoom ?? layer.minZoom;
      const labelSourceLayer =
        layer.source.type === 'pmtiles' ? (layer.source.labelLayer ?? layer.source.sourceLayer) : null;
      map.addLayer({
        id: ids.label,
        type: 'symbol',
        source: labelSourceId,
        ...(labelSourceLayer ? { 'source-layer': labelSourceLayer } : {}),
        ...(layer.filter ? { filter: layer.filter as unknown as Filter } : {}),
        ...(labelMin !== null ? { minzoom: labelMin } : {}),
        layout: {
          visibility: 'none',
          'text-field': labelText(layer.labelProps),
          'text-font': LABEL_FONT,
          'text-size': 11,
          'text-max-width': 9,
          'text-padding': 4,
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': color,
          'text-halo-color': 'rgba(255, 255, 255, 0.92)',
          'text-halo-width': 1.6,
        },
      });
    }
    added.push({ layer, ids: [ids.hit, ids.casing, ids.line, ids.label], hitId: ids.hit });
  }

  const visible = new Set<string>();

  return {
    setVisible: (id, isVisible) => {
      const entry = added.find((a) => a.layer.id === id);
      if (!entry) return;
      if (isVisible) visible.add(id);
      else visible.delete(id);
      for (const layerId of entry.ids) {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
        }
      }
    },

    setReferenceData: (slug, geojson) => {
      const source = map.getSource(`overlay-ref-${slug}`);
      if (source && source.type === 'geojson') {
        (source as maplibregl.GeoJSONSource).setData(geojson);
      }
      const labels = map.getSource(`overlay-ref-${slug}-labels`);
      if (labels && labels.type === 'geojson') {
        (labels as maplibregl.GeoJSONSource).setData(labelPoints(geojson));
      }
    },

    namesAt: (point) => {
      const hitIds = added
        .filter((a) => visible.has(a.layer.id) && map.getLayer(a.hitId))
        .map((a) => a.hitId);
      if (!hitIds.length) return [];
      const out: PlaceName[] = [];
      const seen = new Set<string>();
      for (const feature of map.queryRenderedFeatures(point, { layers: hitIds })) {
        const entry = added.find((a) => a.hitId === feature.layer.id);
        if (!entry || seen.has(entry.layer.id)) continue;
        const name = nameOf(feature.properties, entry.layer.labelProps);
        if (!name) continue;
        seen.add(entry.layer.id);
        // "Draft B: District 2" -- a bare district name would read like the plan's own.
        const shown = entry.layer.group === 'district_maps' ? `${entry.layer.label}: ${name}` : name;
        out.push({ layerId: entry.layer.id, layerLabel: entry.layer.label, name: shown, color: entry.layer.style.color });
      }
      // Catalog order: district maps first, then legislative, census, tribal.
      const order = new Map(layers.map((l, i) => [l.id, i]));
      return out.sort((a, b) => (order.get(a.layerId) ?? 0) - (order.get(b.layerId) ?? 0));
    },

    destroy: () => {
      for (const entry of added) {
        for (const layerId of entry.ids) if (map.getLayer(layerId)) map.removeLayer(layerId);
      }
      for (const sourceId of sources) if (map.getSource(sourceId)) map.removeSource(sourceId);
    },
  };
}
